import { FC } from 'react';
import clsx from 'clsx';
import type { Message } from '@theraply/api-client';
import { format, isToday, isYesterday, parseISO } from 'date-fns';

export interface MessageBubbleProps {
  message: Message;
  isOwnMessage: boolean;
  showAvatar?: boolean;
  showSender?: boolean;
  className?: string;
}

export const MessageBubble: FC<MessageBubbleProps> = ({
  message,
  isOwnMessage,
  showAvatar = true,
  showSender = true,
  className,
}) => {
  const formatTimestamp = (timestamp: string) => {
    const date = parseISO(timestamp);
    if (isToday(date)) {
      return format(date, 'h:mm a');
    } else if (isYesterday(date)) {
      return `Yesterday ${format(date, 'h:mm a')}`;
    } else {
      return format(date, 'MMM d, h:mm a');
    }
  };

  const isSystemMessage = message.senderType === 'SYSTEM';

  if (isSystemMessage) {
    return (
      <div className={clsx('flex justify-center my-4', className)}>
        <div className="bg-gray-100 text-gray-600 text-sm px-4 py-2 rounded-full max-w-md text-center">
          {message.content}
        </div>
      </div>
    );
  }

  return (
    <div
      className={clsx(
        'flex gap-3 mb-4',
        isOwnMessage ? 'flex-row-reverse' : 'flex-row',
        className
      )}
    >
      {/* Avatar */}
      {showAvatar && (
        <div className="flex-shrink-0">
          {message.senderAvatar ? (
            <img
              src={message.senderAvatar}
              alt={message.senderName || 'User'}
              className="w-8 h-8 rounded-full object-cover"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 text-sm font-medium">
              {message.senderName ? message.senderName.charAt(0).toUpperCase() : '?'}
            </div>
          )}
        </div>
      )}

      {/* Message content */}
      <div className={clsx('flex flex-col max-w-[70%]', isOwnMessage ? 'items-end' : 'items-start')}>
        {/* Sender name */}
        {showSender && !isOwnMessage && message.senderName && (
          <div className="text-sm text-gray-600 mb-1 px-1">{message.senderName}</div>
        )}

        {/* Message bubble */}
        <div
          className={clsx(
            'rounded-2xl px-4 py-2 break-words',
            isOwnMessage
              ? 'bg-primary-600 text-white rounded-br-md'
              : 'bg-gray-100 text-gray-900 rounded-bl-md'
          )}
        >
          <div className="whitespace-pre-wrap">{message.content}</div>
        </div>

        {/* Timestamp and read status */}
        <div
          className={clsx(
            'flex items-center gap-2 text-xs text-gray-500 mt-1 px-1',
            isOwnMessage && 'flex-row-reverse'
          )}
        >
          <span>{formatTimestamp(message.sentAt)}</span>
          {isOwnMessage && (
            <span className="flex items-center">
              {message.readAt ? (
                <svg
                  className="w-4 h-4 text-primary-600"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg
                  className="w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

MessageBubble.displayName = 'MessageBubble';
